import FileDownloader from "@/components/FileDownloader/FileDownloader";
import useTranslation from "@/hooks/useTranslation";
import {
  PRODUCT_BY,
  ProductBy,
  useProductImpactDetailExport,
  useProductImpactDetailPerCategoryExport,
  useProductImpactDetailPerScopeExport,
} from "@/pages/dashboard/hooks/useProductImpact";
import { ImpactMetric } from "@/pages/dashboard/utils/types";
import { DownloadOutlined } from "@ant-design/icons";
import { Segmented, Tabs } from "antd";
import { useState } from "react";
import Widget from "../../../components/Widget/Widget";
import ProductImpactDetailsPerLifecycle from "./ProductImpactDetailsPerLifecycle";
import ProductImpactDetailsPerScope from "./ProductImpactDetailsPerScope";

export type FacilityImpactDetailMode = "Absolute" | "Relative (%)";
export type TabKey = "life_cycle" | "scope" | "category";

export default function ProductImpactSideBySide({ metric }: { metric: ImpactMetric }) {
  const translation = useTranslation();
  const [mode, setMode] = useState<FacilityImpactDetailMode>("Absolute");
  const [by, setBy] = useState<ProductBy>("total");
  const [key, setKey] = useState<TabKey>("life_cycle");

  const exportDetail = useProductImpactDetailExport({ by, metric: metric.key });
  const exportDetailPerCategory = useProductImpactDetailPerCategoryExport({ by });
  const exportDetailPerScope = useProductImpactDetailPerScopeExport({ by });

  const items = [
    {
      key: "life_cycle",
      label: "Life cycle phase",
      children: (
        <ProductImpactDetailsPerLifecycle
          metric={metric}
          mode={mode}
          by={by}
        />
      ),
    },
  ];

  if (metric.key === "carbon_footprint") {
    items.push({
      key: "scope",
      label: "Impact scope",
      children: (
        <ProductImpactDetailsPerScope
          by={by}
          mode={mode}
        />
      ),
    });
  }

  return (
    <Widget
      title={"Impact per product"}
      tooltip={translation["dashboard"]["product"]["per_product"]}
      topRight={
        <>
          <Segmented
            options={PRODUCT_BY}
            onChange={setBy}
            value={by}
          />
          <Segmented
            options={["Absolute", "Relative (%)"]}
            onChange={setMode}
          />
          {key === "life_cycle" && (
            <FileDownloader
              fileUrlFn={() => exportDetail.mutateAsync()}
              buttonText={<DownloadOutlined />}
              fileOutputName="product-impact-per-lifecycle.xlsx"
              addTimestampAndCompany={true}
            />
          )}
          {key === "scope" && (
            <FileDownloader
              fileUrlFn={() => exportDetailPerScope.mutateAsync()}
              buttonText={<DownloadOutlined />}
              fileOutputName="product-impact-per-scope.xlsx"
              addTimestampAndCompany={true}
            />
          )}
          {key === "category" && (
            <FileDownloader
              fileUrlFn={() => exportDetailPerCategory.mutateAsync()}
              buttonText={<DownloadOutlined />}
              fileOutputName="product-impact-per-category.xlsx"
              addTimestampAndCompany={true}
            />
          )}
        </>
      }
    >
      <Tabs
        onChange={(key) => setKey(key as TabKey)}
        activeKey={key}
        items={items}
      />
    </Widget>
  );
}
